import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UploadDataComponent } from './steps/upload-data/upload-data.component';
import { PhoneColumnComponent } from './steps/phone-column/phone-column.component';
import { ComposeComponent } from './steps/compose/compose.component';
import { ReviewComponent } from './steps/review/review.component';
import { SendProgressComponent } from './steps/send/send-progress.component';
import { SummaryComponent } from './partials/summary/summary.component';
import { PromptCardComponent } from '../../../components/toasts-and-cards/prompt-card/prompt-card.component';
import {
  SmartBroadcastDraftInfo,
  SmartBroadcastService,
} from '../../../services/smart-broadcast/smart-broadcast.service';
import { SmartBroadcastStep } from '../../../types/smartBroadcast';
import { LeaveCheck } from './smart-broadcast-leave.guard';
import { KeycloakAdminService } from '../../../services/keycloak/keycloak.service';
import { formatCount, formatWhen } from '../../../../utils/sms';

type DialogKind = 'leave' | 'restart';

/**
 * Smart Broadcast: upload a sheet, pick the phone column, write a message
 * that merges the sheet's columns as `{{column}}` fields, review exactly what
 * each row receives, then send and watch it go out.
 */
@Component({
  selector: 'app-smart-broadcast',
  standalone: true,
  imports: [
    UploadDataComponent,
    PhoneColumnComponent,
    ComposeComponent,
    ReviewComponent,
    SendProgressComponent,
    SummaryComponent,
    PromptCardComponent,
  ],
  templateUrl: './smart-broadcast.component.html',
  styleUrl: './smart-broadcast.component.css',
})
export class SmartBroadcastComponent implements OnInit, OnDestroy, LeaveCheck {
  store = inject(SmartBroadcastService);
  private kcService = inject(KeycloakAdminService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @ViewChild('stepsNav') stepsNav?: ElementRef<HTMLElement>;
  @ViewChild('stepHost') stepHost?: ElementRef<HTMLElement>;

  readonly steps: { label: string; title: string }[] = [
    { label: 'Upload', title: 'Upload the sheet' },
    { label: 'Columns', title: 'Choose the phone column' },
    { label: 'Message', title: 'Write the message' },
    { label: 'Review', title: 'Review and send' },
    { label: 'Send', title: 'Sending' },
  ];

  /** A draft kept on this device from an earlier visit. */
  draft: SmartBroadcastDraftInfo | null = null;
  /** The broadcast was still in memory from earlier in this session. */
  resumedInMemory = false;
  /**
   * Text for the page's polite live region. The region is always in the
   * page and only its text changes, so screen readers announce it.
   */
  liveMessage = '';
  private liveTimer?: ReturnType<typeof setTimeout>;
  /** Leaving because the session ended: clear the store when the page closes. */
  private forgetOnDestroy = false;

  dialog: DialogKind | null = null;
  private leaveResolve?: (leave: boolean) => void;

  readonly formatCount = formatCount;

  readonly canStartOver = computed(
    () => this.store.hasWork() && !this.store.sent() && !this.store.loading()
  );

  ngOnInit() {
    // Unsent work from earlier in this session carries on, but only for the
    // user who started it: the store outlives a sign-out in the same tab.
    // Anything else starts from a clean slate, with a kept draft offered back.
    if (this.store.hasWork() && !this.store.sent() && this.store.ownedByCurrentUser()) {
      this.resumedInMemory = true;
      this.announce('Picked up where you left off. Nothing has been sent yet.');
      return;
    }
    this.store.reset();
    this.draft = this.store.peekDraft();
  }

  ngOnDestroy() {
    clearTimeout(this.liveTimer);
    this.store.flushDraft();
    this.leaveResolve?.(true);
    if (this.forgetOnDestroy && !this.store.loading()) this.store.reset();
  }

  /** Set the live region's text after the page has rendered it empty. */
  private announce(message: string) {
    if (!this.isBrowser) return;
    clearTimeout(this.liveTimer);
    this.liveMessage = '';
    this.liveTimer = setTimeout(() => (this.liveMessage = message), 300);
  }

  // ---- steps ----------------------------------------------------------------
  canGo(index: number): boolean {
    if (this.store.loading()) return false;
    if (this.store.sent()) return index === 4;
    return index <= this.store.reachable() && index < 4;
  }

  go(index: number) {
    if (!this.canGo(index) || index === this.store.step()) return;
    this.resumedInMemory = false;
    this.store.goTo(index as SmartBroadcastStep);
    this.revealSteps();
  }

  /** Step 'Step 2: Choose the phone column, done' for screen readers. */
  stepLabel(index: number): string {
    const done = index < this.store.step() || (index === 4 && !!this.store.sent());
    return `Step ${index + 1}: ${this.steps[index].title}${done ? ', done' : ''}`;
  }

  next() {
    this.go(this.store.step() + 1);
  }

  back() {
    this.go(this.store.step() - 1);
  }

  onSent() {
    this.resumedInMemory = false;
    this.revealSteps();
  }

  startAnother() {
    this.store.reset();
    this.draft = null;
    this.revealSteps();
  }

  /**
   * After a step change: bring the step list back into view when it is above
   * the fold, and move focus to the new step's heading. The button that
   * changed the step usually belonged to the step that just went away, so
   * focus would otherwise fall back to the top of the document.
   */
  private revealSteps() {
    if (!this.isBrowser) return;
    requestAnimationFrame(() => {
      const nav = this.stepsNav?.nativeElement;
      if (nav && nav.getBoundingClientRect().top < 0) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        nav.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
      const heading = this.stepHost?.nativeElement.querySelector<HTMLElement>('h2[tabindex="-1"]');
      heading?.focus({ preventScroll: true });
    });
  }

  // ---- kept draft -------------------------------------------------------------
  draftWhen(): string {
    return this.draft ? formatWhen(this.draft.draft.savedAt) : '';
  }

  resumeDraft() {
    if (!this.draft) return;
    this.store.restoreDraft(this.draft);
    this.draft = null;
    this.revealSteps();
  }

  discardDraft() {
    this.store.clearDraft();
    this.draft = null;
    this.revealSteps();
  }

  // ---- leaving and starting over ----------------------------------------------
  /** Called by smartBroadcastLeaveGuard before the router leaves the page. */
  canLeave(): boolean | Promise<boolean> {
    if (!this.store.hasWork() || this.store.sent()) return true;
    // Signed out or session ended: the token is already gone, so staying
    // would leave the user here without a session. Keep the draft on the
    // device and take the broadcast out of memory once the page closes.
    if (!this.signedIn()) {
      this.store.flushDraft();
      this.forgetOnDestroy = true;
      return true;
    }
    this.leaveResolve?.(false);
    this.dialog = 'leave';
    return new Promise<boolean>((resolve) => (this.leaveResolve = resolve));
  }

  private signedIn(): boolean {
    try {
      return this.kcService.isAuthenticated;
    } catch {
      return true;
    }
  }

  askStartOver() {
    this.dialog = 'restart';
  }

  get dialogTitle(): string {
    return this.dialog === 'restart' ? 'Start over?' : 'Leave this broadcast?';
  }

  get dialogAction(): string {
    return this.dialog === 'restart' ? 'Discard broadcast' : 'Leave';
  }

  get dialogClose(): string {
    return this.dialog === 'restart' ? 'Keep working' : 'Stay';
  }

  get dialogBody(): string {
    const subject = this.store.subjectSig().trim();
    if (this.dialog !== 'restart') {
      // Mid-send: the upload carries on whether or not the page stays open.
      if (this.store.loading()) {
        return `Your broadcast is being sent now. If you leave, it still goes out, but you won’t see its progress here. You can follow it in Messages under “${subject}”.`;
      }
      if (this.store.sent()) {
        return `Your broadcast is with the server and keeps going out if you leave. You can follow it in Messages under “${subject}”.`;
      }
    }
    const sentNote = this.store.sendUnclear()
      ? `The last send may have reached the server. Look in Messages for “${subject}” before sending again.`
      : 'Nothing has been sent.';
    if (this.dialog === 'restart') {
      return `This clears the sheet, the message and the draft kept on this device. ${sentNote}`;
    }
    if (this.store.hasRows() && !this.store.sheetKept()) {
      return `${sentNote} Your message is kept on this device, but the sheet is too large to keep, so you would upload it again.`;
    }
    return `${sentNote} Your draft stays on this device, so you can carry on from Custom SMS later.`;
  }

  closeDialog(confirmed: boolean) {
    const kind = this.dialog;
    this.dialog = null;
    if (kind === 'restart') {
      if (confirmed) {
        this.store.discard();
        this.draft = null;
        this.resumedInMemory = false;
        this.revealSteps();
      }
      return;
    }
    const resolve = this.leaveResolve;
    this.leaveResolve = undefined;
    resolve?.(confirmed);
  }

  /** Refreshing or closing the tab loses what the browser cannot keep. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    this.store.flushDraft();
    const atRisk =
      this.store.loading() || (this.store.hasRows() && !this.store.sent() && !this.store.sheetKept());
    if (atRisk) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
